/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag
 */
import React, { useCallback, useState } from 'react';

import { useIntl } from 'react-intl-next';

import { IconButton } from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import { aiProactiveMessages } from '@atlaskit/editor-common/ai-messages';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import Heading from '@atlaskit/heading';
import CrossIcon from '@atlaskit/icon/core/cross';
import CoreMoreIcon from '@atlaskit/icon/core/show-more-horizontal';
import Lozenge from '@atlaskit/lozenge';
import { fg } from '@atlaskit/platform-feature-flags';
import { Flex, Inline } from '@atlaskit/primitives/compiled';

import type { AIProactivePluginConfig } from '../../../aiProactivePluginType';
import { clearSelectRecommendation } from '../../../pm-plugins/ai-proactive/commands';

import { SuggestionSetting } from './setting/suggestion-setting';

const styles = cssMap({
	heading: {
		flex: 1,
	},

	afterElementWrapper: {
		alignItems: 'center',
	},
});

type HeadingItemProps = {
	config: AIProactivePluginConfig;
	editorView: EditorView;
	onClearSelectRecommendation: () => void;
	onClose: () => void;
};
const toolTipProps: React.ComponentProps<typeof IconButton>['tooltip'] = { position: 'top' };

type ContextPanelAfterElementProps = {
	config: AIProactivePluginConfig;
	editorView: EditorView;
};

export const ContextPanelAfterElement = (props: ContextPanelAfterElementProps) => {
	const { formatMessage } = useIntl();
	const { editorView } = props;
	const { handleFeedbackSubmission, product } = props.config;

	const onClearSelectRecommendation = React.useCallback(() => {
		const { state, dispatch } = editorView;
		const command = clearSelectRecommendation();
		command(state, dispatch);
	}, [editorView]);

	const [isOpen, setIsOpen] = useState(false);
	const toggleModal = useCallback(() => {
		setIsOpen(!isOpen);
		onClearSelectRecommendation();
	}, [isOpen, onClearSelectRecommendation]);

	return (
		<>
			<Flex justifyContent="space-between" xcss={styles.afterElementWrapper}>
				<Lozenge>{formatMessage(aiProactiveMessages.betaLabel)}</Lozenge>
				{fg('platform_editor_ai_context_panel_more_menu') && (
					<>
						<DropdownMenu<HTMLButtonElement>
							trigger={({ triggerRef, ...props }) => (
								<IconButton
									// Ignored via go/ees005
									// eslint-disable-next-line react/jsx-props-no-spreading
									{...props}
									appearance="subtle"
									label={formatMessage(aiProactiveMessages.moreMenuLabel)}
									icon={CoreMoreIcon}
									ref={triggerRef}
								/>
							)}
							shouldRenderToParent
							placement="bottom-end"
						>
							<DropdownItemGroup>
								<DropdownItem onClick={toggleModal}>
									{formatMessage(aiProactiveMessages.moreMenuManageSuggestions)}
								</DropdownItem>

								<DropdownItem
									onClick={() => {
										if (!handleFeedbackSubmission) {
											return;
										}
										handleFeedbackSubmission({
											sentiment: 'recommendation-feedback',
											editorAttributes: { product },
										});
									}}
								>
									{formatMessage(aiProactiveMessages.moreMenuGiveFeedback)}
								</DropdownItem>
							</DropdownItemGroup>
						</DropdownMenu>
						<SuggestionSetting isOpen={isOpen} toggleModal={toggleModal} editorView={editorView} />
					</>
				)}
			</Flex>
		</>
	);
};

// Remove HeadingItem while cleaning platform_editor_ai_object_sidebar_injection
export const HeadingItem = (props: HeadingItemProps) => {
	const { formatMessage } = useIntl();
	const { onClose, editorView, onClearSelectRecommendation } = props;
	const closeTooltip = formatMessage(aiProactiveMessages.closeButtonLabel);
	const { handleFeedbackSubmission, product } = props.config;

	const [isOpen, setIsOpen] = useState(false);
	const toggleModal = useCallback(() => {
		setIsOpen(!isOpen);
		onClearSelectRecommendation();
	}, [isOpen, onClearSelectRecommendation]);

	return (
		<Inline space="space.100" alignBlock="center">
			<Inline xcss={styles.heading} space="space.100" alignBlock="center">
				<Heading size="medium" color="color.text">
					{formatMessage(aiProactiveMessages.suggestionsTitle)}
				</Heading>
				<Lozenge>{formatMessage(aiProactiveMessages.betaLabel)}</Lozenge>
			</Inline>

			<Inline>
				{fg('platform_editor_ai_context_panel_more_menu') && (
					<>
						<DropdownMenu<HTMLButtonElement>
							trigger={({ triggerRef, ...props }) => (
								<IconButton
									// Ignored via go/ees005
									// eslint-disable-next-line react/jsx-props-no-spreading
									{...props}
									appearance="subtle"
									label={formatMessage(aiProactiveMessages.moreMenuLabel)}
									icon={CoreMoreIcon}
									ref={triggerRef}
								/>
							)}
							shouldRenderToParent
							placement="bottom-end"
						>
							<DropdownItemGroup>
								<DropdownItem onClick={toggleModal}>
									{formatMessage(aiProactiveMessages.moreMenuManageSuggestions)}
								</DropdownItem>

								<DropdownItem
									onClick={() => {
										if (!handleFeedbackSubmission) {
											return;
										}
										handleFeedbackSubmission({
											sentiment: 'recommendation-feedback',
											editorAttributes: { product },
										});
									}}
								>
									{formatMessage(aiProactiveMessages.moreMenuGiveFeedback)}
								</DropdownItem>
							</DropdownItemGroup>
						</DropdownMenu>
						<SuggestionSetting isOpen={isOpen} toggleModal={toggleModal} editorView={editorView} />
					</>
				)}
				<IconButton
					appearance="subtle"
					label={closeTooltip}
					icon={CrossIcon}
					onClick={onClose}
					isTooltipDisabled={false}
					tooltip={toolTipProps}
				/>
			</Inline>
		</Inline>
	);
};
